import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch, Controller } from 'react-hook-form';
import type { Provider } from '@echo-work-platform/shared-types';
import { api } from '@/lib/api-client';
import { CrudTable, type Column } from '@/components/shared/crud-table';
import { Select } from '@/components/shared/select';

interface FormValues {
  name: string;
  type: 'OPENAI' | 'AZURE_OPENAI' | 'ANTHROPIC' | 'BEDROCK' | 'ECHOPROXY';
  baseUrl: string;
  apiKeySecret: string;
  apiVersion: string;
  region: string;
}

const BEDROCK_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-2',
  'eu-west-1',
  'eu-west-3',
  'eu-central-1',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'ap-northeast-1',
  'ap-southeast-1',
  'ap-southeast-2',
];

function ProviderForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: Partial<Provider> | null;
  onSubmit: (v: Partial<Provider>) => void;
  onCancel: () => void;
}): JSX.Element {
  const { register, handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      name: initial?.name ?? '',
      type: initial?.type ?? 'OPENAI',
      baseUrl: initial?.baseUrl ?? '',
      apiKeySecret: initial ? '••••••••' : '',
      apiVersion: initial?.apiVersion ?? '',
      region: initial?.region ?? 'us-east-1',
    },
  });
  const watchedType = useWatch({ control, name: 'type' });
  const isAzure = watchedType === 'AZURE_OPENAI';
  const isBedrock = watchedType === 'BEDROCK';
  // EchoProxy providers are toggle-only: no base URL / key / region at the
  // provider level — those live on each EchoProxy model.
  const isEchoProxy = watchedType === 'ECHOPROXY';

  return (
    <form
      className="space-y-4"
      onSubmit={(e) =>
        void handleSubmit((v) => {
          const payload: Partial<Provider> = {
            name: v.name,
            type: v.type,
            baseUrl: isAzure ? v.baseUrl || null : null,
            apiVersion: isAzure ? v.apiVersion || null : null,
            region: isBedrock ? v.region || null : null,
          };
          // EchoProxy has no provider-level key; for all other types send the key
          // only when the operator actually entered/changed it.
          if (!isEchoProxy && v.apiKeySecret && v.apiKeySecret !== '••••••••') {
            payload.apiKeySecret = v.apiKeySecret;
          }
          onSubmit(payload);
        })(e)
      }
    >
      <div>
        <label className="label">Név</label>
        <input className="input" {...register('name', { required: true })} />
      </div>
      <div>
        <label className="label">Típus</label>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: 'OPENAI', label: 'OpenAI' },
                { value: 'AZURE_OPENAI', label: 'Azure OpenAI' },
                { value: 'ANTHROPIC', label: 'Anthropic' },
                { value: 'BEDROCK', label: 'Bedrock' },
                { value: 'ECHOPROXY', label: 'EchoProxy' },
              ]}
            />
          )}
        />
      </div>
      {isAzure && (
        <div>
          <label className="label">
            Endpoint <span className="text-rose-500">*</span>
          </label>
          <input
            className="input"
            {...register('baseUrl', { required: true })}
            placeholder="https://<resource>.openai.azure.com/?api-version=2024-12-01-preview"
          />
          <p className="mt-1 text-xs text-slate-500">
            Az Azure OpenAI resource URL-je. A backend ebből nyeri ki a <code>resourceName</code>-et és (ha
            tartalmazza) az <code>api-version</code>-t is — query-paraméterként megadhatod a URL végén.
          </p>
        </div>
      )}
      {isEchoProxy ? (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Az EchoProxy provider csak ki/be kapcsolható — nincs provider-szintű URL vagy kulcs. A deployment
          URL-t és az API kulcsot az egyes modelleknél add meg.
        </p>
      ) : (
        <div>
          <label className="label">
            API Key
            {isBedrock && (
              <span className="ml-2 text-xs font-normal text-slate-500">(Bedrock: ABSK… Bearer token)</span>
            )}
          </label>
          <input className="input" type="password" {...register('apiKeySecret', { required: !initial })} />
          {initial && (
            <p className="mt-1 text-xs text-slate-500">Hagyd üresen a meglévő kulcs megtartásához.</p>
          )}
        </div>
      )}
      {isBedrock && (
        <div>
          <label className="label">
            AWS Region <span className="text-rose-500">*</span>
          </label>
          <Controller
            control={control}
            name="region"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={BEDROCK_REGIONS.map((r) => ({ value: r, label: r }))}
              />
            )}
          />
          <p className="mt-1 text-xs text-slate-500">
            EU inference profile-okhoz (<code>eu.*</code>) használj eu-* régiót. Globálishoz (
            <code>global.*</code>) us-east-1.
          </p>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Mégse
        </button>
        <button type="submit" className="btn-primary">
          Mentés
        </button>
      </div>
    </form>
  );
}

export function ModelProviders(): JSX.Element {
  const queryClient = useQueryClient();

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<Provider>(`/api/admin/providers/${id}`, { isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/providers'] });
      // Deactivating a provider deactivates its active models too (EW-191 cascade), so the
      // flat model list must refetch as well.
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/models-flat'] });
    },
    onError: () => {
      // A failed toggle can mean the provider changed elsewhere (e.g. it was
      // deleted) — refetch so the table shows the real state.
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/providers'] });
      alert('Nem sikerült átállítani a providert. Próbáld újra.');
    },
  });

  const toggleActive = (provider: Provider): void => {
    // The deactivate text names the EW-191 cascade: the backend deactivates every active model
    // of the provider in the same transaction. Activation is elemental and touches no model.
    const message = provider.isActive
      ? `A(z) "${provider.name}" provider és minden aktív modellje inaktívvá válik — a modellek nem jutnak el a felhasználókhoz, a kötéseik viszont megmaradnak. A provider újraaktiválása után a modelleket a Models oldalon egyenként kell újra aktiválnod.\n\nDeaktiválod?`
      : `A(z) "${provider.name}" provider aktiválása önmagában nem aktiválja a modelljeit — a modelleket a Models oldalon egyenként kell újra aktiválnod.\n\nAktiválod?`;
    if (confirm(message)) setActive.mutate({ id: provider.id, isActive: !provider.isActive });
  };

  const columns: Column<Provider>[] = [
    { key: 'name', label: 'Név' },
    { key: 'type', label: 'Típus' },
    {
      key: 'baseUrl',
      label: 'Endpoint / Region',
      render: (r) =>
        r.type === 'BEDROCK'
          ? (r.region ?? '—')
          : r.type === 'ECHOPROXY'
            ? '— (a modellnél)'
            : (r.baseUrl ?? '—'),
    },
    {
      key: 'isActive',
      label: 'Állapot',
      render: (r) => (
        <button
          title={r.isActive ? 'Kattints a deaktiváláshoz' : 'Kattints az aktiváláshoz'}
          disabled={setActive.isPending}
          onClick={() => toggleActive(r)}
          className={
            r.isActive
              ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100'
              : 'rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-200'
          }
        >
          {r.isActive ? 'Aktív' : 'Inaktív'}
        </button>
      ),
    },
  ];

  return (
    <CrudTable<Provider>
      title="Providers"
      endpoint="/api/admin/providers"
      columns={columns}
      itemTitle={(p) => p.name}
      // model.provider_id is RESTRICT (EW-191): the 409 means the provider still has models.
      deleteConflictMessage="Nem törölhető: a szolgáltatónak még vannak modelljei. Töröld a modelleket a Models oldalon, vagy inaktiváld a providert helyette."
      renderForm={(initial, onSubmit, onCancel) => (
        <ProviderForm initial={initial} onSubmit={onSubmit} onCancel={onCancel} />
      )}
    />
  );
}
